<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">

    <div class="bg-white dark:bg-slate-900 w-full max-w-6xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText class="w-5 h-5 text-brand-600" />
            {{ form.id ? 'Editar Solicitud' : 'Nueva Solicitud de Crédito' }}
          </h2>
          <p class="text-xs text-slate-500">Configura el préstamo basado en los parámetros del producto.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="flex-1 overflow-hidden flex flex-col md:flex-row">

        <div class="w-full md:w-5/12 p-6 overflow-y-auto custom-scrollbar border-r border-slate-100 dark:border-slate-800">
          <form @submit.prevent="handleSubmit" id="creditForm" class="space-y-5">

            <div class="relative">
              <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Buscar Cliente</label>
              <div class="relative">
                <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  v-model="customerSearchQuery"
                  @focus="showCustomerDropdown = true"
                  type="text"
                  placeholder="Escribe nombre o documento..."
                  :class="[inputClasses, 'pl-9']"
                />
              </div>

              <div v-if="showCustomerDropdown && filteredCustomers.length > 0" class="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                <div
                  v-for="c in filteredCustomers"
                  :key="c.id"
                  @click="selectCustomer(c)"
                  class="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer border-b border-slate-100 dark:border-slate-700/50 last:border-0"
                >
                  <div class="font-bold text-sm text-slate-900 dark:text-slate-100">{{ c.first_name }} {{ c.last_name }}</div>
                  <div class="text-[10px] font-mono text-slate-500">{{ c.identity_document || 'Sin Doc' }}</div>
                </div>
              </div>

              <div v-if="selectedCustomer" class="mt-2 flex items-center justify-between bg-white border border-brand-100 p-2 rounded-lg">
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 rounded-full bg-brand-200 text-brand-700 flex items-center justify-center text-[10px] font-bold">
                    {{ selectedCustomer.first_name[0] }}
                  </div>
                  <span class="text-xs font-bold text-brand-800">{{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }}</span>
                </div>
                <button @click="clearCustomer" type="button" class="text-brand-400 hover:text-brand-600">
                  <X class="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Producto Financiero</label>
              <select v-model="form.product_id" required :class="inputClasses" @change="onProductChange">
                <option value="" disabled>Seleccionar Producto</option>
                <option v-for="p in products" :key="p.id" :value="p.id">
                  {{ p.name }} ({{ p.interest_type === 'FLAT' ? 'Flat' : 'Normal' }} - Sugerida: {{ p.interest_rate }}%)
                </option>
              </select>
              <div v-if="selectedProduct" class="mt-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-[10px] text-slate-500 space-y-1">
                <div class="flex justify-between"><span>Monto Permitido:</span> <span class="font-bold">${{ selectedProduct.min_amount }} - ${{ selectedProduct.max_amount }}</span></div>
                <div class="flex justify-between"><span>Plazo Permitido (Meses):</span> <span class="font-bold">{{ selectedProduct.min_term_months }} a {{ selectedProduct.max_term_months }}</span></div>
                <div class="flex justify-between"><span>Tasa Permitida (%):</span> <span class="font-bold">{{ selectedProduct.min_interest_rate }}% a {{ selectedProduct.max_interest_rate }}%</span></div>
              </div>
            </div>

            <!-- NUEVOS CAMPOS: DESTINO Y ANALISTA -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Destino del Crédito</label>
                <select v-model="form.destination" required :class="inputClasses">
                  <option value="" disabled>Seleccionar Destino</option>
                  <option v-for="d in destinations" :key="d.value" :value="d.value">{{ d.text }}</option>
                </select>
              </div>
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Analista Asignado</label>
                <select v-model="form.adviser" required :class="inputClasses">
                  <option value="" disabled>Seleccionar Analista</option>
                  <option v-for="u in users" :key="u.id" :value="u.id">{{ u.full_name }}</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Monto Solicitado</label>
                <div class="relative">
                  <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                  <input v-model.number="form.amount" type="number" step="0.01" :class="[inputClasses, 'pl-8 font-bold', amountError ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500 text-red-600' : 'text-slate-900 dark:text-white']" />
                </div>
                <p v-if="amountError" class="text-[9px] text-red-500 mt-1 font-bold">{{ amountError }}</p>
              </div>
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Tasa Anual Aplicada (%)</label>
                <input v-model.number="form.rate" type="number" step="0.01" :class="[inputClasses, rateError ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500 text-red-600' : 'bg-white dark:bg-slate-800']" />
                <p v-if="rateError" class="text-[9px] text-red-500 mt-1 font-bold">{{ rateError }}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Plazo (Meses)</label>
                <input v-model.number="form.term_months" type="number" min="1" :class="[inputClasses, termError ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500 text-red-600' : '']" />
                <p v-if="termError" class="text-[9px] text-red-500 mt-1 font-bold">{{ termError }}</p>
              </div>
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Frecuencia de Pago</label>
                <select v-model.number="form.condition_level" :class="inputClasses">
                  <option :value="1">Diario</option>
                  <option :value="2">Semanal</option>
                  <option :value="3">Quincenal</option>
                  <option :value="4">Mensual</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
               <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Fecha de Desembolso</label>
                <input v-model="form.init_date" type="date" :class="inputClasses" />
              </div>
              <div class="flex flex-col justify-end">
                <div class="bg-brand-50 border border-brand-100 text-white font-bold text-sm rounded-xl py-2 px-4 flex items-center justify-center h-[38px]">
                  {{ calculatedPeriods }} Cuotas en Total
                </div>
              </div>
            </div>

            <div class="bg-slate-50 dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs text-slate-500">Total a Pagar (Aprox):</span>
                <span class="font-bold text-brand-600 text-xl">${{ form.total_amount }}</span>
              </div>
              <div class="flex justify-between items-center border-t border-slate-200 dark:border-slate-700 pt-2">
                <span class="text-xs text-slate-500">Fecha de Última Cuota:</span>
                <span class="text-xs font-mono font-bold">{{ formatDateDisplay(form.end_date) }}</span>
              </div>
            </div>

          </form>
        </div>

        <div class="w-full md:w-7/12 bg-slate-50 dark:bg-slate-900/50 flex flex-col h-full">
          <div class="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex justify-between items-center">
            <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Calendar class="w-4 h-4 text-brand-500" /> Plan de Pagos
            </h3>
            <span v-if="selectedProduct" class="text-[10px] font-bold px-2 py-1 bg-brand-100 text-brand-700 rounded uppercase">
              Tipo de Tasa: {{ selectedProduct.interest_type }}
            </span>
          </div>

          <!-- Agregamos max-h-[500px] (o la altura que prefieras) y mantenemos overflow-y-auto -->
          <div class="flex-1 overflow-y-auto custom-scrollbar p-0 max-h-[600px] border dark:border-slate-800 rounded-xl">
              <table class="w-full text-xs text-left border-collapse">
                  <!-- El sticky top-0 funcionará siempre que este contenedor tenga el overflow -->
                  <thead class="bg-slate-100 dark:bg-slate-800 text-slate-500 uppercase sticky top-0 z-10 shadow-sm">
                      <tr>
                          <th class="px-3 py-2">#</th>
                          <th class="px-3 py-2">Fecha</th>
                          <th class="px-3 py-2 text-center">Días</th>
                          <th class="px-3 py-2 text-right">Capital</th>
                          <th class="px-3 py-2 text-right">Interés</th>
                          <th class="px-3 py-2 text-right">Cuota</th>
                          <th class="px-3 py-2 text-right">Saldo</th>
                      </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-200 dark:divide-slate-800 bg-white dark:bg-slate-900">
                      <tr v-if="paymentPlan.length === 0">
                          <td colspan="7" class="px-4 py-12 text-center text-slate-400">
                              <Calendar class="w-8 h-8 mx-auto mb-2 opacity-50" />
                              Completa el formulario y corrige errores para generar la tabla...
                          </td>
                      </tr>
                      <tr v-for="item in paymentPlan" :key="item.number" class="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                          <td class="px-3 py-2 font-mono text-slate-500">{{ item.number }}</td>
                          <td class="px-3 py-2 text-slate-700 dark:text-slate-300 font-medium">{{ formatDateDisplay(item.date) }}</td>
                          <td class="px-3 py-2 text-center text-slate-400 font-mono">{{ item.days }}</td>
                          <td class="px-3 py-2 text-right text-slate-500">${{ item.capital }}</td>
                          <td class="px-3 py-2 text-right text-amber-600">${{ item.interest }}</td>
                          <td class="px-3 py-2 text-right font-bold text-slate-900 dark:text-white">${{ item.total }}</td>
                          <td class="px-3 py-2 text-right font-mono text-slate-400">${{ item.balance }}</td>
                      </tr>
                  </tbody>
                  <!-- El sticky bottom-0 mantendrá los totales siempre visibles al final del scroll -->
                  <tfoot v-if="paymentPlan.length > 0" class="bg-slate-100 dark:bg-slate-800 font-bold sticky bottom-0 z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.05)]">
                      <tr>
                          <td colspan="3" class="px-3 py-3 text-right">TOTALES</td>
                          <td class="px-3 py-3 text-right">${{ totalMetrics.capital }}</td>
                          <td class="px-3 py-3 text-right text-amber-600">${{ totalMetrics.interest }}</td>
                          <td class="px-3 py-3 text-right text-brand-600">${{ form.total_amount }}</td>
                          <td class="px-3 py-3 text-right"></td>
                      </tr>
                  </tfoot>
              </table>
          </div>
        </div>

      </div>

      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 z-20">
        <button
          @click="$emit('close')"
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          @click="handlePrintSummary"
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-2"
        ><Printer class="w-4 h-4" />
          Imprimir
        </button>
        <button
          type="submit"
          form="creditForm"
          :disabled="isSaving || paymentPlan.length === 0 || amountError !== null || termError !== null || rateError !== null"
          class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          Guardar Solicitud
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';
import { X, Save, Loader2, FileText, Calendar, Search, Printer } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import { useSerials } from '@/composables/useSerials';
import { useInformativeSummary } from '@/composables/reports/useInformativeSummary';
const { generateSummary } = useInformativeSummary();

const { generateCode, isLoadingSerial } = useSerials();
const props = defineProps({ show: Boolean, applicationToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const inputClasses = "w-full px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-700 dark:text-slate-200";

// --- ESTADOS ---
const customers = ref([]);
const products = ref([]);
const users = ref([]); // Lista de analistas
const destinations = ref([]); // Destinos de tabla globals
const paymentPlan = ref([]);
const isSaving = ref(false);

const selectedProduct = ref(null);

// Estados de Error
const amountError = ref(null);
const termError = ref(null);
const rateError = ref(null);

const customerSearchQuery = ref('');
const showCustomerDropdown = ref(false);
const selectedCustomer = ref(null);
let loanCodeGenerated=null;
const form = reactive({
  id: null,
  customer_id: '',
  product_id: '',
  adviser: '', // Nuevo campo
  destination: '', // Nuevo campo
  term_months: 1,
  condition_level: 4,
  amount: 0,
  rate: 0,
  init_date: new Date().toISOString().split('T')[0],
  end_date: null,
  total_amount: '0.00',
  comments: ''
});

const totalMetrics = reactive({ capital: '0.00', interest: '0.00' });

// --- CARGA INICIAL ---
onMounted(async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user?.company) return;
  try {
    const [resCust, resProd, resDest, resUsers] = await Promise.all([
      apiClient.get(`/customers?company_id=${user.company}`),
      apiClient.get(`/credit_products?company_id=${user.company}`),
      apiClient.get(`/globals?type=7`), // Carga de destinos (Type 7)
      apiClient.get(`/users?company=${user.company}`) // Carga de usuarios analistas
    ]);
    customers.value = resCust.data.data || resCust.data;
    products.value = resProd.data.data || resProd.data;
    destinations.value = resDest.data.data || resDest.data;
    users.value = resUsers.data.data || resUsers.data;
  } catch (e) { console.error(e); }
});

watch(() => props.show, (val) => {
  if (val) {
    if (props.applicationToEdit && Object.keys(props.applicationToEdit).length > 0) {
      const data = props.applicationToEdit;
      Object.assign(form, data);

      // Restauramos los valores visuales
      selectedCustomer.value = customers.value.find(c => c.id == data.customer_id) || null;
      selectedProduct.value = products.value.find(p => p.id == data.product_id) || null;
      loanCodeGenerated = data.code;
      if (!selectedCustomer.value && data.customer_id) {
        const parts = (data.customer_name || 'Cliente ').split(' ');
        selectedCustomer.value = {
          id: data.customer_id,
          first_name: parts[0] || 'C',
          last_name: parts.slice(1).join(' ') || ''
        };
      }
      // NOTA: El motor de amortización (generarPlanDePagos) se disparará
      // automáticamente gracias al watch múltiple que ya tienes configurado.
    } else {
      // Limpiar formulario si es un registro nuevo
      Object.assign(form, {
        id: null, customer_id: '', product_id: '', adviser: '', destination: '',
        term_months: 1, condition_level: 4, amount: 0, rate: 0,
        init_date: new Date().toISOString().split('T')[0], end_date: null, total_amount: '0.00', comments: ''
      });
      selectedCustomer.value = null;
      selectedProduct.value = null;
      paymentPlan.value = [];
    }
  }
});
// --- BUSCADOR CLIENTES ---
const filteredCustomers = computed(() => {
  if (!customerSearchQuery.value) return customers.value.slice(0, 5);
  const q = customerSearchQuery.value.toLowerCase();
  return customers.value.filter(c =>
    c.first_name?.toLowerCase().includes(q) ||
    c.last_name?.toLowerCase().includes(q) ||
    c.identity_document?.includes(q)
  );
});

const selectCustomer = (customer) => {
  selectedCustomer.value = customer;
  form.customer_id = customer.id;
  customerSearchQuery.value = '';
  showCustomerDropdown.value = false;
};

const clearCustomer = () => {
  selectedCustomer.value = null;
  form.customer_id = '';
};

watch(customerSearchQuery, (val) => { if(val) showCustomerDropdown.value = true; });

// --- LÓGICA DE PRODUCTO Y VALIDACIONES ---
const onProductChange = () => {
  selectedProduct.value = products.value.find(p => p.id === form.product_id);
  if (selectedProduct.value) {
    form.rate = selectedProduct.value.interest_rate;
    validateAll();
  }
};

const validateAmount = () => {
  amountError.value = null;
  if (!selectedProduct.value) return;
  const amt = Number(form.amount);
  const min = Number(selectedProduct.value.min_amount);
  const max = Number(selectedProduct.value.max_amount);
  if (amt < min) amountError.value = `El monto mínimo es $${min}`;
  else if (amt > max) amountError.value = `El monto máximo es $${max}`;
};

const validateTerm = () => {
  termError.value = null;
  if (!selectedProduct.value) return;
  const term = Number(form.term_months);
  const min = Number(selectedProduct.value.min_term_months || 1);
  const max = Number(selectedProduct.value.max_term_months || 120);
  if (term < min) termError.value = `El plazo mínimo es ${min} mes(es)`;
  else if (term > max) termError.value = `El plazo máximo es ${max} meses`;
};

const validateRate = () => {
  rateError.value = null;
  if (!selectedProduct.value) return;
  const rate = Number(form.rate);
  const min = Number(selectedProduct.value.min_interest_rate || 0);
  const max = Number(selectedProduct.value.max_interest_rate || 100);
  if (rate < min) rateError.value = `La tasa mínima es ${min}%`;
  else if (rate > max) rateError.value = `La tasa máxima es ${max}%`;
};

const validateAll = () => {
  validateAmount();
  validateTerm();
  validateRate();
};

// --- CÁLCULO DE CUOTAS ---
const calculatedPeriods = computed(() => {
  const m = Number(form.term_months) || 0;
  switch(Number(form.condition_level)) {
    case 1: return m * 20;
    case 2: return m * 4;
    case 3: return m * 2;
    case 4: return m * 1;
    default: return m;
  }
});

const getPeriodsPerYear = (freq) => {
  switch (Number(freq)) {
    case 1: return 360;
    case 2: return 48;
    case 3: return 24;
    case 4: return 12;
    default: return 12;
  }
};

// --- MOTOR DE AMORTIZACIÓN ---
const generarPlanDePagos = () => {
  paymentPlan.value = [];
  totalMetrics.capital = '0.00';
  totalMetrics.interest = '0.00';
  form.total_amount = '0.00';

  if(amountError.value || termError.value || rateError.value || !selectedProduct.value || form.amount <= 0 || form.term_months <= 0) return;

  const principal = parseFloat(form.amount);
  const annualRate = parseFloat(form.rate) / 100;
  const periods = calculatedPeriods.value;
  const interestType = selectedProduct.value.interest_type;

  let baseInstallment = 0;
  let flatInterestPerPeriod = 0;
  let totalInterestFlat = 0;

  if (interestType === 'FLAT') {
    totalInterestFlat = principal * annualRate;
    flatInterestPerPeriod = Math.floor((totalInterestFlat / periods) * 100) / 100;
  } else {
    const periodsPerYear = getPeriodsPerYear(form.condition_level);
    const theoreticalRatePerPeriod = annualRate / periodsPerYear;
    if (theoreticalRatePerPeriod > 0) {
      baseInstallment = principal * (theoreticalRatePerPeriod / (1 - Math.pow(1 + theoreticalRatePerPeriod, -periods)));
    } else {
      baseInstallment = principal / periods;
    }
  }

  let currentDate = new Date(form.init_date);
  currentDate.setMinutes(currentDate.getMinutes() + currentDate.getTimezoneOffset());
  let previousDate = new Date(currentDate);

  let remainingBalance = principal;
  let accumulatedInterest = 0;
  let sumCapital = 0;
  let sumInterest = 0;

  for (let i = 1; i <= periods; i++) {
    switch (Number(form.condition_level)) {
      case 1: currentDate.setDate(currentDate.getDate() + 1); break;
      case 2: currentDate.setDate(currentDate.getDate() + 7); break;
      case 3: currentDate.setDate(currentDate.getDate() + 15); break;
      case 4: currentDate.setMonth(currentDate.getMonth() + 1); break;
    }

    const diffTime = Math.abs(currentDate - previousDate);
    const days = Math.round(diffTime / (1000 * 60 * 60 * 24));

    let interest = 0;
    let capital = 0;

    if (interestType === 'FLAT') {
      if (i === periods) {
        interest = totalInterestFlat - accumulatedInterest;
      } else {
        interest = flatInterestPerPeriod;
      }
      capital = principal / periods;
      remainingBalance -= capital;
    } else {
      interest = remainingBalance * annualRate * (days / 365);
      capital = baseInstallment - interest;
      remainingBalance -= capital;
    }

    if (i === periods) {
      if (interestType !== 'FLAT') {
        capital += remainingBalance;
      }
      remainingBalance = 0;
    }

    accumulatedInterest += interest;

    const rowTotal = capital + interest;
    sumCapital += capital;
    sumInterest += interest;

    paymentPlan.value.push({
      number: i,
      date: currentDate.toISOString().split('T')[0],
      days: days,
      capital: capital.toFixed(2),
      interest: interest.toFixed(2),
      total: rowTotal.toFixed(2),
      balance: Math.max(0, remainingBalance).toFixed(2),
      payed: 0
    });

    previousDate = new Date(currentDate);
  }

  if(paymentPlan.value.length > 0) {
    form.end_date = paymentPlan.value[paymentPlan.value.length - 1].date;
  }

  totalMetrics.capital = sumCapital.toFixed(2);
  totalMetrics.interest = sumInterest.toFixed(2);
  form.total_amount = (sumCapital + sumInterest).toFixed(2);
};

// --- WATCHERS ---
watch(() => form.amount, validateAmount);
watch(() => form.term_months, validateTerm);
watch(() => form.rate, validateRate);

watch(
  () => [form.amount, form.rate, form.term_months, form.product_id, form.condition_level, form.init_date],
  () => {
    generarPlanDePagos();
  }
);

const formatDateDisplay = (isoStr) => {
  if (!isoStr) return '--';
  const [y, m, d] = isoStr.split('-');
  return `${d}/${m}/${y}`;
};

const handleSubmit = async () => {
    if (!form.customer_id) return showError('Debe seleccionar un cliente.');
    if (!form.destination) return showError('Debe seleccionar un destino del crédito.');
    if (!form.adviser) return showError('Debe seleccionar un analista.');
    if (amountError.value || termError.value || rateError.value) return showError('Corrija los errores de validación antes de guardar.');

    isSaving.value = true;
    try {
        const user = SecureStorage.getItem('llevo_user');

        const payload = {
            ...form,
            term: calculatedPeriods.value,
            company_id: user.company,
            payment_plan: paymentPlan.value // SIEMPRE enviamos el plan nuevo
        };

       /*  delete payload.term_months;
        delete payload.condition_level_name;
        delete payload.code;
        delete payload.customer_name;
        delete payload.customer_doc;
        delete payload.customer_code;
        delete payload.contract_type;
        delete payload.product_name;
        delete payload.interest_type;
        delete payload.analista; */
        if (selectedProduct.value && selectedProduct.value.selected_charges) {
            payload.selected_charges = selectedProduct.value.selected_charges;
        }

        if (form.id) {
            // ES EDICIÓN (PUT)
            await apiClient.put(`/loans/process/${form.id}`, payload);
            showSuccess('Solicitud y plan de pagos actualizados correctamente');
        } else {
            // ES NUEVO (POST)
            const loanCode = await generateCode('LOAN', {
                productId: selectedProduct.value.code
            });
            loanCodeGenerated = loanCode;
            payload.code = loanCode;

            await apiClient.post('/loans/process', payload);
            showSuccess('Crédito y plan generados correctamente');
        }

        handlePrintSummary();
        emit('saved');
        emit('close');
    } catch (e) {
        console.error(e);
        showError('Error al procesar el crédito. La transacción fue revertida.');
    } finally {
        isSaving.value = false;
    }
};

const handlePrintSummary = () => {
  // 1. Validamos que tengamos lo mínimo para el reporte
  if (!selectedCustomer.value || !selectedProduct.value || paymentPlan.value.length === 0) {
    showError('Complete la información y genere el plan de pagos antes de imprimir.');
    return;
  }
  const fakeloan=form;
  fakeloan.code=loanCodeGenerated?loanCodeGenerated:'BORRADOR';
  fakeloan.term=calculatedPeriods;

  const formattedPlan = paymentPlan.value.map(plan => ({
    number: plan.number,
    due_date: plan.date,
    days: plan.days,
    total_amount: Number(plan.total),       // Mapeado de 'total'
    principal: Number(plan.capital),        // Mapeado de 'capital'
    interest: Number(plan.interest),        // Mapeado de 'interest'
    late_interest: 0,                       // Por ser plan teórico
    charges: 0,                             // Por ser plan teórico
    balance: Number(plan.balance),
    status: 'PENDIENTE'
  }));
  // 2. Construimos el "Dossier Ficticio" (Mock object)
  const fakeDossier = {
    // Información del Cliente
    customer: {
      ...selectedCustomer.value,
      full_name: `${selectedCustomer.value.first_name} ${selectedCustomer.value.last_name}`,
    },
    // Información del Crédito (Lo que está en el form)
    loan:fakeloan,
    // El producto seleccionado
    product: {
      ...selectedProduct.value
    },
    // El plan de pagos que acabas de calcular en el motor de amortización
    theoretical_plans: formattedPlan,
    // Si necesitas resoluciones o comentarios vacíos para que no truene el PDF
    resolutions: [],
    guaranties: [],
    transactions: []
  };

  // 3. Enviamos al generador
  try {
    generateSummary(
      fakeDossier,
      {
        showPerfil: true,
        showProducto: false,
        showPlanTeorico: true, // Este es el que nos interesa
        // Desactivamos lo que no existe en un borrador
        showResolucion: true,
        showEvaluacion: false,
        showGarantias: false,
        showPlanActivo: false,
        showTransacciones: false,
        showAnexos: false,
      },
      // Pasamos los catálogos si los tienes, si no un objeto vacío
      {},
      'SOLICITÚD DE CRÉDITO'
    );
  } catch (e) {
    console.error("Error al generar PDF:", e);
    showError("No se pudo generar el documento.");
  }
};

// Función auxiliar para que el PDF sepa qué frecuencia es
const getFrequencyName = (level) => {
  const levels = { 1: 'Diario', 2: 'Semanal', 3: 'Quincenal', 4: 'Mensual' };
  return levels[level] || 'Mensual';
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>